import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import JSZip from 'jszip';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
} from '@material-ui/core';
import { buildBaselineKMLBody } from '../../../algorithms/utils/turf';

const CSV_MIME = 'text/csv';
const KML_MIME = 'application/vnd.google-earth.kml+xml';

/**
 * @function downloadBlob
 * @description Hands a Blob to the browser as a file download, via a temporary anchor that is
 * clicked and then cleaned up along with its object URL.
 * @param {Blob} blob - The file contents.
 * @param {string} filename - Name offered to the user.
 * **Called by:
 * @see {zipAndDownload}
 * @see {handleExport} - for single-file exports.
 * @memberof module:ShoreS
 * @returns {void}
 */
const downloadBlob = (blob, filename) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

/**
 * @function csvContent
 * @description Serializes uniform rows to CSV, taking the column order from the first row's keys.
 * @param {Array<Object>} rows - Records sharing the same keys.
 * **Called by:
 * @see {ShoreSExportDialog} - via the `files` memo.
 * @memberof module:ShoreS
 * @returns {string} The CSV text, empty when there are no rows.
 */
const csvContent = (rows) => {
  if (!rows.length) return '';
  const cols = Object.keys(rows[0]);
  const lines = [cols.join(',')];
  rows.forEach((row) => {
    lines.push(cols.map((c) => row[c]).join(','));
  });
  return lines.join('\n');
};

/**
 * @function toISO
 * @description Renders a Date as an ISO string, passing non-Date values through untouched.
 * @param {Date|string} value - The value to render.
 * **Called by:
 * @see {buildPairsRows}
 * @memberof module:ShoreS
 * @returns {string} The ISO string, or the original value.
 */
const toISO = (value) => (value instanceof Date ? value.toISOString() : value);

/**
 * @function buildPairsRows
 * @description Flattens the merged modeled/reference distance pairs into CSV-ready rows with
 * snake_case column names.
 * @param {Array<Object>} mergedDistances - Output of `mergeByTimeProximity`.
 * **Called by:
 * @see {ShoreSExportDialog} - via the `files` memo.
 * @memberof module:ShoreS
 * @returns {Array<Object>} One row per pair.
 */
const buildPairsRows = (mergedDistances) =>
  (mergedDistances || []).map((r) => ({
    transect_id: r.transectId,
    time: toISO(r.time),
    distance_modeled: r.distanceModeled,
    time_reference: toISO(r.timeReference),
    distance_reference: r.distanceReference,
  }));

/**
 * @function buildMetricsRows
 * @description Turns the error-metrics object into metric/value rows, blanking out any
 * non-finite value so it doesn't reach the CSV as NaN or Infinity.
 * @param {Object} errorMetrics - The computed error metrics.
 * **Called by:
 * @see {ShoreSExportDialog} - via the `files` memo.
 * @memberof module:ShoreS
 * @returns {Array<Object>} One row per metric.
 */
const buildMetricsRows = (errorMetrics) =>
  errorMetrics
    ? Object.entries(errorMetrics).map(([metric, value]) => ({
        metric,
        value: Number.isFinite(value) ? value : '',
      }))
    : [];

/**
 * @function wrapKMLDocument
 * @description Wraps a KML body in the standard XML declaration and `<kml><Document>` envelope.
 * @param {string} body - The KML placemarks.
 * **Called by:
 * @see {ShoreSExportDialog} - via the `files` memo.
 * @memberof module:ShoreS
 * @returns {string} A complete KML document.
 */
const wrapKMLDocument = (body) =>
  `<?xml version="1.0" encoding="UTF-8"?>
  <kml xmlns="http://www.opengis.net/kml/2.2">
    <Document>${body}</Document>
  </kml>`;

/**
 * @function zipAndDownload
 * @description Bundles the selected files into a single zip and downloads it, the path taken
 * whenever more than one export is checked.
 * @param {Array<Object>} files - `{ name, content }` entries.
 * @param {string} zipName - Name offered to the user.
 * **Called by:
 * @see {handleExport}
 * @memberof module:ShoreS
 * @returns {Promise<void>}
 */
const zipAndDownload = async (files, zipName) => {
  const zip = new JSZip();
  files.forEach(({ name, content }) => zip.file(name, content));
  const blob = await zip.generateAsync({ type: 'blob' });
  downloadBlob(blob, zipName);
};

/**
 * @function ShoreSExportDialog
 * @description Exports the Shore-S validation results: the raw modeled x reference distance
 * pairs (CSV), a summary of the scalar error metrics (CSV), and the drawn baseline (KML). Any
 * single selected file downloads directly; two or more are zipped together. Modeled on
 * `ShoreRExportDialog.jsx`'s manual-CSV + JSZip pattern (the repo has no CSV library dependency).
 * @param {Object} props - Component props.
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {Function} props.close - Closes the dialog.
 * **Called by:
 * @see {ShoreSAnalysisSteps}
 * @memberof module:ShoreS
 * @returns {JSX.Element} The export dialog.
 */
const ShoreSExportDialog = ({ open, close }) => {
  const [t] = useTranslation();
  const mergedDistances = useSelector((s) => s.shoreSAnalysis.mergedDistances);
  const errorMetrics = useSelector((s) => s.shoreSAnalysis.errorMetrics);
  const baselineCoords = useSelector((s) => s.shoreSAnalysis.baselineCoords);

  const [selection, setSelection] = useState({ pairs: true, metrics: true, baseline: true });

  const hasPairs = Boolean(mergedDistances?.length);
  const hasMetrics = Boolean(errorMetrics);
  // A LineString needs at least two vertices to be worth exporting.
  const hasBaseline = Boolean(baselineCoords?.length >= 2);

  const toggle = (key) => () => setSelection((prev) => ({ ...prev, [key]: !prev[key] }));

  const files = useMemo(() => {
    const out = [];
    if (selection.pairs && hasPairs) {
      out.push({
        name: 'shore-s-pares-modelado-referencia.csv',
        content: csvContent(buildPairsRows(mergedDistances)),
        mime: CSV_MIME,
      });
    }
    if (selection.metrics && hasMetrics) {
      out.push({
        name: 'shore-s-metricas-erro.csv',
        content: csvContent(buildMetricsRows(errorMetrics)),
        mime: CSV_MIME,
      });
    }
    if (selection.baseline && hasBaseline) {
      out.push({
        name: 'shore-s-linha-base.kml',
        content: wrapKMLDocument(buildBaselineKMLBody(baselineCoords, t('forms.shoreS.exports.baselineFolder'))),
        mime: KML_MIME,
      });
    }
    return out;
  }, [selection, hasPairs, hasMetrics, hasBaseline, mergedDistances, errorMetrics, baselineCoords, t]);

  /**
   * @function handleExport
   * @description Downloads whatever the user checked: a single file goes out as-is, more than one
   * is bundled into a zip first.
   * **Called by:
   * @see {ShoreSExportDialog} - via the "Export" button's onClick.
   * @memberof module:ShoreS
   * @returns {Promise<void>}
   */
  const handleExport = async () => {
    if (!files.length) return;
    if (files.length === 1) {
      downloadBlob(new Blob([files[0].content], { type: files[0].mime }), files[0].name);
      return;
    }
    await zipAndDownload(files, 'shore-s-export.zip');
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth onClose={close}>
      <DialogTitle>{t('forms.shoreS.exports.title')}</DialogTitle>
      <DialogContent>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={selection.pairs} onChange={toggle('pairs')} disabled={!hasPairs} />}
            label={t('forms.shoreS.exports.pairs')}
          />
          <FormControlLabel
            control={<Checkbox checked={selection.metrics} onChange={toggle('metrics')} disabled={!hasMetrics} />}
            label={t('forms.shoreS.exports.metrics')}
          />
          <FormControlLabel
            control={<Checkbox checked={selection.baseline} onChange={toggle('baseline')} disabled={!hasBaseline} />}
            label={t('forms.shoreS.exports.baselineKml')}
          />
        </FormGroup>
        <Box mt={2}>
          <Button variant="outlined" color="primary" onClick={handleExport} disabled={!files.length}>
            {t('forms.shoreS.exports.downloadCsv')}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={close}>
          {t('forms.shoreR.exports.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShoreSExportDialog;
